/**
 * The right-hand end of an overlay analysis panel's header: the analysed
 * layer's name, then the PDF-report button.
 *
 * `DeviationVolumePanel` and `SurfaceDifferencePanel` stack in one column and
 * the owner has now asked five times for their two headers to read as one
 * instrument. Every previous round unified them by editing the same markup in
 * two files, and every round they drifted again — the layer name had a
 * `text-right flex-1` on one card and a `min-w-0` inside a flex row on the
 * other, and only one of the two ever grew a report button.
 *
 * So there is no markup to keep in step any more: both panels pass this one
 * component as `DockPanel`'s `actions`, and "the two headers are identical" is
 * true by construction rather than by review.
 *
 * The name TRUNCATES and is never wrapped: `DockPanel`'s toggle (chevron,
 * title, collapsed summary) is `shrink-0` because the summary is the whole
 * point of the collapsed state, so this side takes the squeeze. The full name
 * is on the `title` attribute for hover.
 */
import type { ReactNode } from 'react';

import { FileTextSolid } from '@mynaui/icons-react';
import { useTranslation } from 'react-i18next';

import { getLinkSessionId } from '@/graphql/execute';
import { useFeature } from '@/hooks/useFeature';
import { useOpenAnalysisReport } from '@/hooks/useOpenAnalysisReport';
import type { ComparisonReportTarget } from '@/lib/analyses/compare/pairing';

/**
 * The square icon button at the right end of a panel header — the report
 * button below and `ClearancePanel`'s dock opener — so the two read as one
 * control family. `shrink-0` is load-bearing (see the report button's note);
 * `aria-pressed:` styles a toggle that is on, and is inert on a plain button.
 */
export const DOCK_HEADER_ICON_BUTTON =
  'inline-flex size-5 pointer-coarse:size-8 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none aria-pressed:bg-slate-200/70 aria-pressed:text-slate-900 dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white dark:aria-pressed:bg-stone-700/70 dark:aria-pressed:text-white';

export interface DockPanelLayerNameProps {
  layerName: string;
  /** Whatever follows the name at the right end — the report button, here. */
  children?: ReactNode;
}

/**
 * The truncating layer name on its own — the element `DockPanelActions`
 * renders, exported so a header that offers no report keeps the same markup
 * rather than a copy of its classes. (`ClearancePanel` used it while the PDF
 * worker had no clearance section; it now carries `DockPanelActions` with
 * `kind='clearance'`, its dock toggle in the `children` slot.)
 */
export const DockPanelLayerName = ({ layerName, children }: DockPanelLayerNameProps) => (
  <div className='flex min-w-0 flex-1 items-center justify-end gap-1'>
    <span className='min-w-0 truncate text-slate-500 dark:text-stone-400' title={layerName}>
      {layerName}
    </span>
    {children}
  </div>
);

export interface DockPanelActionsProps {
  projectId: string;
  /** The analysed layer — named in the header, and the report's subject. */
  layerId: string;
  layerName: string;
  /**
   * Which analysis produced this layer — a deviation layer's metadata has
   * `deviationStats`, a surface-difference layer's has `stationVolumes`, a
   * clearance layer's has `clearance`. Determines which report section
   * `useOpenAnalysisReport` asks for; using the wrong one fails server-side
   * with an error naming the OTHER analysis kind (the bug this prop was added
   * to fix). `clearance` asks for the whole-run summary (`CLEARANCE`).
   */
  kind: 'deviation' | 'surfaceDifference' | 'clearance';
  /**
   * Set on a panel of a paired compare-surfaces run whose two halves both
   * exist (spec §4.3, ruling R27): the button asks for the run's ONE combined
   * report — the same document from either panel — instead of this layer's.
   */
  comparison?: ComparisonReportTarget;
  /**
   * More header icons, rendered AFTER the report button — `ClearancePanel`'s
   * dock toggle. Use `DOCK_HEADER_ICON_BUTTON` so they read as one family.
   */
  children?: ReactNode;
}

export const DockPanelActions = ({
  projectId,
  layerId,
  layerName,
  kind,
  comparison,
  children,
}: DockPanelActionsProps) => {
  const { t } = useTranslation('project');
  const { t: tFg } = useTranslation('featureGate');
  const reportGate = useFeature('report.pdf');
  const isSharedLink = !!getLinkSessionId();
  // The handler, the dialog and the soft paywall all live in the hook — a
  // report opened from a closure inside a panel is unmountable, and that is
  // precisely how a report path once shipped unreachable here.
  const openReport = useOpenAnalysisReport();

  // Which tier the tooltip names when the gate is shut. Product line first
  // (a PRO gate is about the product, not the plan size), then the two
  // storage rungs, then "any paid plan".
  const requirement =
    reportGate.gate.minProduct === 'PRO'
      ? tFg('requirement.pro')
      : reportGate.gate.minStorageBytes != null && reportGate.gate.minStorageBytes >= 1_000_000_000_000
        ? tFg('requirement.business')
        : reportGate.gate.minStorageBytes != null
          ? tFg('requirement.team')
          : tFg('requirement.paid');

  return (
    <DockPanelLayerName layerName={layerName}>
      {/* Muted, never `disabled`: CLAUDE.md's gating rule — the HTML attribute
          swallows the click that would open the upgrade dialog. The click
          itself always reaches the hook, which prompts the upgrade rather than
          opening the dialog when the gate is shut. Never on a shared link:
          a report is an EDITOR job it can't start. */}
      {!isSharedLink && (
      <button
        type='button'
        data-testid='dock-report-button'
        title={reportGate.allowed ? t('report.button') : tFg('tooltip.requires', { requirement })}
        // `shrink-0` is load-bearing, not decoration. This button is a flex
        // ITEM next to a `flex-1` name that wants all the space it can get, so
        // without it the button is a shrinkable box and `size-5` sets only its
        // BASIS: the deviation panel (longer title, longer layer name) squeezed
        // it to 14 px wide while the surface panel rendered the same button at
        // its full 20 px — measured in a browser on 2026-09-05, same component,
        // same `size-5`, two different widths on screen. A square icon button
        // must never be the thing that yields.
        className={`${DOCK_HEADER_ICON_BUTTON}${reportGate.allowed ? '' : ' opacity-50'}`}
        onClick={() =>
          openReport(
            comparison
              ? {
                  mode: 'comparison',
                  projectId,
                  groupId: comparison.groupId,
                  layerId: comparison.cutFillLayerId,
                  layerName: comparison.cutFillLayerName,
                }
              : { mode: kind, projectId, layerId, layerName },
          )
        }>
        <FileTextSolid className='size-3.5' />
      </button>
      )}
      {children}
    </DockPanelLayerName>
  );
};
